import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Shared hover-tooltip help (the s30 shared-ui standard, item 9 — shipped early with s30 item 5
// because NEW fields must use it). A small info icon beside a field label; the balloon opens on
// hover AND keyboard focus AND tap (so it degrades cleanly on touch and assistive tech), closes on
// mouse-leave / blur / Escape / a second tap. Replaces inline caption help for NEW fields; the
// existing-form migration is its own sweep (04 #32). No em dashes in tooltip copy (locked rule).
//
// 04 #76(b) (s99): the balloon renders through a PORTAL to document.body, positioned from the
// button's viewport rect. The prior absolutely-positioned balloon was clipped by any
// overflow-hidden ancestor, and ConfirmDialog's panel is one, so every tooltip inside a dialog
// (the refund dialog's Return-mode line caps, the apply dialog) was unreadable. Fixed positioning
// escapes the clip without touching the dialog's overflow model; the left edge is clamped to the
// viewport and the balloon flips below the button when there is no room above.
const TIP_WIDTH = 256; // w-64
const EDGE = 8;

export function InfoTip({ text }: { text: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const tipRef = useRef<HTMLSpanElement | null>(null);
  const id = useId();

  // Position after the balloon renders (its height is content-driven, needed for the flip check).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const btn = btnRef.current;
    const tip = tipRef.current;
    if (!btn || !tip) return;
    const r = btn.getBoundingClientRect();
    const h = tip.getBoundingClientRect().height;
    const left = Math.min(Math.max(EDGE, r.left + r.width / 2 - TIP_WIDTH / 2), Math.max(EDGE, window.innerWidth - TIP_WIDTH - EDGE));
    const below = r.top - h - 6 < EDGE; // not enough room above: flip under the button
    const top = below ? r.bottom + 6 : r.top - h - 6;
    setPos({ top, left, below });
  }, [open, text]);

  return (
    <span className="relative inline-flex align-middle" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        ref={btnRef}
        type="button"
        aria-label="More information"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onClick={(e) => {
          // Inside a <label>, keep the tap from activating the label's input.
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="ml-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[rgb(var(--edge-rgb)/0.25)] font-serif text-[10px] italic leading-none text-[var(--color-muted)] transition hover:border-[rgb(var(--edge-rgb)/0.50)] hover:text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
      >
        i
      </button>
      {open
        ? createPortal(
            <span
              ref={tipRef}
              role="tooltip"
              id={id}
              style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: TIP_WIDTH, visibility: pos ? 'visible' : 'hidden' }}
              className="z-[60] rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--color-background)] px-3 py-2 text-left text-xs font-normal normal-case leading-snug text-[var(--color-text)] shadow-lg"
            >
              {text}
            </span>,
            document.body,
          )
        : null}
    </span>
  );
}
